/* CANFOTO ETAP 2 R6-2H-F — FROZEN SNAPSHOT INTEGRITY GUARD
   Pure validation owner. No scoring/ranking/F-P/setup/commerce/share mutation. */
(()=>{
'use strict';
const VERSION='CANFOTO-ETAP2-R6-2H-F-FROZEN-INTEGRITY-GUARD-V1';
const clean=v=>String(v??'').replace(/\s+/g,' ').trim();
const clone=v=>JSON.parse(JSON.stringify(v));
function stable(value){
  if(Array.isArray(value)) return value.map(stable);
  if(value&&typeof value==='object'){
    return Object.keys(value).sort().reduce((o,k)=>(o[k]=stable(value[k]),o),{});
  }
  return value;
}
async function sha256Object(value){
  const text=JSON.stringify(stable(value));
  const bytes=new TextEncoder().encode(text);
  const buf=await crypto.subtle.digest('SHA-256',bytes);
  return [...new Uint8Array(buf)].map(x=>x.toString(16).padStart(2,'0')).join('');
}
function engineFromOwner(owner){
  if(clean(owner)==='GDX_PANEL_LED_V4') return 'PANEL_LED_V4';
  if(clean(owner)==='GDX_COB_LED_V5') return 'COB_LED_V5';
  return '';
}
function ids(product){
  return (product?.top5||[]).slice().sort((a,b)=>Number(a.rank)-Number(b.rank)).map(r=>clean(r.setupRecordId));
}
async function validateProduct({engineOwner,index,snapshotApi,productHandle,indexRowOverride,snapshotProductOverride,snapshotOverride}={}){
  const handle=clean(productHandle);
  const row=indexRowOverride!==undefined?indexRowOverride:(index?.getProduct?.(handle)||null);
  const product=snapshotProductOverride!==undefined?snapshotProductOverride:(snapshotApi?.getProduct?.(handle)||null);
  const snapshot=snapshotOverride!==undefined?snapshotOverride:(snapshotApi?.snapshot||null);
  const expectedEngine=engineFromOwner(engineOwner||row?.engineOwner);
  const top5=(product?.top5||[]).slice().sort((a,b)=>Number(a.rank)-Number(b.rank));
  const setupIds=top5.map(r=>clean(r.setupRecordId));

  let computedPayloadHash='';
  if(product){
    const payload=clone(product);
    delete payload.payloadHash;
    try{computedPayloadHash=await sha256Object(payload)}catch(_){}
  }

  const checks={
    handlePresent:!!handle,
    indexReady:!!index,
    snapshotApiReady:!!snapshotApi,
    indexRowPresent:!!row,
    snapshotProductPresent:!!product,
    snapshotEnvelopePresent:!!snapshot,
    productionModeExact:clean(index?.productionMode)==='FROZEN_CANONICAL_SNAPSHOT',
    snapshotVersionExact:clean(snapshotApi?.version)===clean(index?.version),
    engineOwnerExact:!!row&&clean(row.engineOwner)===clean(engineOwner||row.engineOwner),
    productEngineExact:!!product&&clean(product.engine)===expectedEngine,
    productHandleExact:!!product&&clean(product.productHandle)===handle,
    materializationVersionExact:!!row&&!!snapshot&&clean(row.materializationVersion)===clean(snapshot.materializationVersion),
    materializationBatchFingerprintExact:!!row&&!!snapshot&&clean(row.materializationBatchFingerprint)===clean(snapshot.materializationBatchFingerprint),
    inputBatchFingerprintExact:!!row&&!!snapshot&&clean(row.inputBatchFingerprint)===clean(snapshot.inputBatchFingerprint),
    sharedV27AdapterVersionExact:
      !!row&&!!snapshot&&
      clean(row.sharedV27AdapterVersion)===clean(snapshot.sharedV27AdapterVersion)&&
      clean(snapshot.sharedV27AdapterVersion)===clean(index?.sharedV27AdapterVersion),
    payloadHashIndexExact:!!row&&!!product&&clean(row.payloadHash)===clean(product.payloadHash),
    payloadHashComputedExact:!!product&&/^[0-9a-f]{64}$/.test(computedPayloadHash)&&computedPayloadHash===clean(product.payloadHash),
    top5CountExact:top5.length===5&&Number(row?.top5Count)===5,
    top5RanksExact:top5.length===5&&top5.every((r,i)=>Number(r.rank)===i+1),
    top5SetupIdsPresentUnique:setupIds.length===5&&setupIds.every(Boolean)&&new Set(setupIds).size===5,
    rowCanonicalIdentityExact:top5.length===5&&top5.every(r=>
      clean(r?.canonicalDto?.setupRecordId)===clean(r.setupRecordId)&&
      clean(r?.sharedV27Projection?.setupRecordId)===clean(r.setupRecordId)
    ),
    rowProjectionReady:top5.length===5&&top5.every(r=>r?.sharedV27Projection?.readyForOffDomV27===true)
  };

  if(expectedEngine==='COB_LED_V5'){
    checks.catalogFingerprintExact=
      !!snapshot&&
      clean(snapshot.catalogFingerprint)===clean(index?.cob?.catalogFingerprint)&&
      clean(product?.catalogFingerprint)===clean(snapshot.catalogFingerprint);
    checks.resolverPolicyExact=
      clean(snapshot?.materializationResolverPolicyVersion)===clean(index?.cob?.materializationResolverPolicyVersion);
  }else if(expectedEngine==='PANEL_LED_V4'){
    checks.panelBatchFingerprintExact=
      clean(snapshot?.materializationBatchFingerprint)===clean(index?.panel?.materializationBatchFingerprint);
  }

  const failedChecks=Object.entries(checks).filter(([,v])=>v!==true).map(([k])=>k);
  return Object.freeze({
    ok:failedChecks.length===0,
    version:VERSION,
    engineOwner:clean(engineOwner||row?.engineOwner),
    productHandle:handle,
    checks:Object.freeze(checks),
    failedChecks:Object.freeze(failedChecks),
    reasonCodes:Object.freeze(failedChecks.map(x=>'FROZEN_INTEGRITY_'+x.toUpperCase())),
    computedPayloadHash,
    expectedPayloadHash:clean(product?.payloadHash),
    indexPayloadHash:clean(row?.payloadHash),
    setupRecordIds:Object.freeze(setupIds)
  });
}
window.CANFOTO_R62HF_FROZEN_INTEGRITY_GUARD=Object.freeze({
  version:VERSION,
  validateProduct,
  sha256Object
});
window.dispatchEvent(new CustomEvent('canfoto:r62hf-frozen-integrity-guard-ready',{detail:{version:VERSION}}));
})();